/**
 * Comercial → Clientes → Perfil do cliente — página cheia (v2).
 *
 * Layout fiel à referência oficial de design:
 *   integracao-nova/crm/titam-crm-lead-historico-notas-v1.png
 * (desenhada para o histórico de Lead; adotada aqui como padrão do perfil de
 * Cliente). Substitui o antigo drawer lateral (ClientePanel, removido) — a
 * navegação agora é uma rota própria (`/financeiro/clientes/:id`).
 *
 * Um Cliente continua sendo um `contact` com >=1 negócio ganho (mesmo modelo
 * do Lead) — reusa os mesmos endpoints (getCrmContact/…Deals/…Activities/
 * …History/updateCrmContact/deleteCrmContact) já usados no restante do CRM.
 *
 * Abas: a antiga "Resumo" virou o conteúdo da aba **Histórico** (métricas +
 * timeline unificada), no layout da referência — composer de nota + chips de
 * filtro (Todos/Notas/Atividades/Alterações) + timeline com ícone por tipo de
 * evento. "Contatos", "Financeiro" e "Observações" saem (conteúdo delas não
 * está na referência); "Atendimentos" entra como aba nova (scaffold
 * compartilhado com o restante do CRM, aguardando a integração de WhatsApp).
 *
 * Notas: não existe tabela de notas dedicada no backend — uma nota é uma
 * Activity com tipo 'mensagem' (tipo já previsto no módulo de Atividades),
 * criada via createCrmContactActivity e exibida pela timeline unificada
 * (getCrmContactHistory já inclui atividades). Editar nota usa updateActivity
 * (é preciso casar o evento do histórico — que não carrega id — com a lista
 * de atividades por título+data para achar o id). Não há endpoint de exclusão
 * de atividade no backend, por isso não há "excluir nota" aqui.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getCrmContact,
  getCrmMembers,
  getCrmContactDeals,
  getCrmContactActivities,
  createCrmContactActivity,
  updateActivity,
  getCrmContactHistory,
  updateCrmContact,
  deleteCrmContact,
  deleteDeal,
  pjListProjects,
  getPipelines,
  getStages,
  fmtBRL,
} from '../../db/api.js'
import {
  HIST_ICON, HIST_COLOR, fmtDate, fmtDateTime,
  DealsTab, ProjetosTab, FilesTab, ActivitiesTab, ServiceTab, ConfirmDialog,
} from '../comercial/panelKit.jsx'
import ClienteFormPanel from '../comercial/ClienteFormPanel.jsx'
import DealPanel from '../comercial/DealPanel.jsx'
import DealModal from '../comercial/DealModal.jsx'
import '../comercial/clienteView.css'

const TABS = [
  { id: 'historico', label: 'Histórico' },
  { id: 'atividades', label: 'Atividades' },
  { id: 'negocios', label: 'Negócios' },
  { id: 'projetos', label: 'Projetos' },
  { id: 'arquivos', label: 'Arquivos' },
  { id: 'atendimentos', label: 'Atendimentos' },
]

const FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'notas', label: 'Notas' },
  { id: 'atividades', label: 'Atividades' },
  { id: 'alteracoes', label: 'Alterações' },
]

const iniciais = (nome) => String(nome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

const AVATAR_CORES = ['#101828', '#2563eb', '#7c5cf0', '#12a06a']
function corAvatar(id) {
  const s = String(id || '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_CORES[h % AVATAR_CORES.length]
}

function origemIcon(origem) {
  const o = (origem || '').toLowerCase()
  if (o.includes('whats')) return 'whats'
  if (o.includes('indic')) return 'star'
  if (o.includes('site')) return 'link'
  if (o.includes('anúncio') || o.includes('anuncio') || o.includes('ads')) return 'ads'
  if (o.includes('evento')) return 'calendar'
  if (o.includes('parceria')) return 'users'
  return 'target'
}

// nota = Activity com tipo 'mensagem' (sem tabela própria); o resto dos tipos
// de atividade (tarefa/evento/follow_up/pagamento) conta como "atividade".
const isNota = (item) => item.tipo === 'atividade' && (item.descricao || '').startsWith('mensagem')

function classifica(item) {
  if (isNota(item)) return 'notas'
  if (item.tipo === 'atividade') return 'atividades'
  if (item.tipo === 'negocio') return 'alteracoes'
  return 'sistema' // 'criado' — só aparece em "Todos"
}

function iconFor(item) {
  if (isNota(item)) return { icon: 'page', color: '#f0a125' }
  return { icon: HIST_ICON[item.tipo] || HIST_ICON.default, color: HIST_COLOR[item.tipo] || HIST_COLOR.default }
}

// Ciclo médio (dias) entre negócios ganhos consecutivos.
function cicloDias(deals) {
  const datas = deals
    .filter((d) => d.status === 'ganho')
    .map((d) => new Date(d.data_fechamento || d.updated_at))
    .filter((d) => !isNaN(d))
    .sort((a, b) => a - b)
  if (datas.length < 2) return null
  let total = 0
  for (let i = 1; i < datas.length; i += 1) total += datas[i] - datas[i - 1]
  return Math.round(total / (datas.length - 1) / 86400000)
}

// ── Aba Histórico: composer de nota + feed unificado ───────────────────────
// Componente de módulo (não aninhado no render de ClienteView) para não
// remontar — e perder o texto da nota em digitação — a cada re-render do pai.
function HistoricoTab({ contactId, user, history, tabsLoading, activityIdByKey, onReload, onError }) {
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [filtro, setFiltro] = useState('todos')
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const salvarNota = async () => {
    if (!noteText.trim() || saving) return
    setSaving(true)
    try {
      await createCrmContactActivity(contactId, {
        titulo: noteText.trim(), tipo: 'mensagem', prioridade: 'baixa', status: 'feito',
        responsavel_id: user?.id || null,
      })
      setNoteText('')
      await onReload()
    } catch (e) { onError(e.message) } finally { setSaving(false) }
  }

  const startEdit = (item) => {
    const key = `${item.titulo}__${new Date(item.occurred_at).getTime()}`
    const activityId = activityIdByKey.get(key)
    if (!activityId) return
    setEditingId(activityId)
    setEditText(item.titulo)
  }

  const saveEdit = async () => {
    if (!editText.trim() || savingEdit) return
    setSavingEdit(true)
    try {
      await updateActivity(editingId, { titulo: editText.trim() })
      setEditingId(null)
      await onReload()
    } catch (e) { onError(e.message) } finally { setSavingEdit(false) }
  }

  const visiveis = history.filter((h) => filtro === 'todos' || classifica(h) === filtro)

  return (
    <div>
      <div className="cv-note-box">
        <div className="cv-note-head">
          <span className="cv-note-title">Adicionar nota</span>
          <button className="cv-note-pin" title="Fixar (em breve)" disabled><Icon n="star" s={14} /></button>
        </div>
        <textarea
          className="cv-note-textarea"
          placeholder="Escreva uma nota sobre este cliente…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
        <div className="cv-note-foot">
          <div className="cv-note-tools">
            <button className="cv-note-tool" disabled title="Anexo (em breve)"><Icon n="clip" s={15} /></button>
            <button className="cv-note-tool" disabled title="Emoji (em breve)"><Icon n="smile" s={15} /></button>
          </div>
          <div className="cv-note-actions">
            <button className="cv-btn cv-btn-ghost" onClick={() => setNoteText('')} disabled={saving || !noteText}>Cancelar</button>
            <button className="cv-btn cv-btn-primary" onClick={salvarNota} disabled={saving || !noteText.trim()}>
              {saving ? 'Salvando…' : 'Salvar nota'}
            </button>
          </div>
        </div>
      </div>

      <div className="cv-filters">
        {FILTROS.map((f) => (
          <button key={f.id} type="button" className={`cv-filter${filtro === f.id ? ' active' : ''}`} onClick={() => setFiltro(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {tabsLoading ? (
        <div className="cv-empty">Carregando…</div>
      ) : !visiveis.length ? (
        <div className="cv-empty">Nenhum evento por aqui ainda.</div>
      ) : (
        <div className="cv-timeline">
          {visiveis.map((item, i) => {
            const { icon, color } = iconFor(item)
            const nota = isNota(item)
            const last = i === visiveis.length - 1
            const key = `${item.titulo}__${new Date(item.occurred_at).getTime()}`
            const canEdit = nota && activityIdByKey.has(key)
            const isEditing = canEdit && editingId === activityIdByKey.get(key)
            return (
              <div key={i} className="cv-tl-item">
                <div className="cv-tl-rail">
                  <div className="cv-tl-icon" style={{ background: `${color}1a`, borderColor: `${color}55`, color }}>
                    <Icon n={icon} s={17} c={color} />
                  </div>
                  {!last && <div className="cv-tl-line" />}
                </div>
                <div className="cv-tl-body">
                  <div className={`cv-tl-card${item.ator ? '' : ' plain'}`}>
                    <div className="cv-tl-head">
                      {item.ator ? (
                        <div className="cv-tl-who">
                          <span className="cv-tl-avatar" style={{ background: corAvatar(item.ator) }}>{iniciais(item.ator)}</span>
                          <span className="cv-tl-name">{item.ator}</span>
                        </div>
                      ) : (
                        <div className="cv-tl-who">
                          <span className="cv-tl-name">{item.titulo}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span className="cv-tl-time">{fmtDateTime(item.occurred_at)}</span>
                        {canEdit && !isEditing && (
                          <span className="cv-tl-hover">
                            <button className="cv-tl-iconbtn" title="Editar nota" onClick={() => startEdit(item)}>
                              <Icon n="edit" s={13} />
                            </button>
                          </span>
                        )}
                      </div>
                    </div>

                    {!item.ator && !nota && item.descricao && <div className="cv-tl-sub">{item.descricao}</div>}

                    {isEditing ? (
                      <div className="cv-tl-edit-box">
                        <textarea
                          className="cv-note-textarea"
                          style={{ minHeight: 70 }}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="cv-tl-edit-actions">
                          <button className="cv-btn cv-btn-ghost" onClick={() => setEditingId(null)} disabled={savingEdit}>Cancelar</button>
                          <button className="cv-btn cv-btn-primary" onClick={saveEdit} disabled={savingEdit || !editText.trim()}>
                            {savingEdit ? 'Salvando…' : 'Salvar'}
                          </button>
                        </div>
                      </div>
                    ) : nota ? (
                      <div className="cv-tl-text">{item.titulo}</div>
                    ) : item.ator ? (
                      <div className="cv-tl-text">{item.titulo}{item.descricao ? ` — ${item.descricao}` : ''}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function ClienteView() {
  const { id: contactId } = useParams()
  const navigate = useNavigate()
  const { selectedClient, user } = useApp()

  const [contact, setContact] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [tab, setTab] = useState('historico')

  const [history, setHistory] = useState([])
  const [activities, setActivities] = useState([])
  const [deals, setDeals] = useState([])
  const [projetos, setProjetos] = useState([])
  const [tabsLoading, setTabsLoading] = useState(true)
  const [infoDealId, setInfoDealId] = useState(null)

  const [editOpen, setEditOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [dealModal, setDealModal] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const load = () => {
    if (!contactId) return
    setLoading(true); setTabsLoading(true); setErr(null)
    getCrmContact(contactId)
      .then(setContact)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false))

    Promise.allSettled([
      getCrmContactHistory(contactId),
      getCrmContactActivities(contactId),
      getCrmContactDeals(contactId),
      pjListProjects().catch(() => []),
    ]).then(([h, a, d, p]) => {
      setHistory(h.status === 'fulfilled' ? h.value : [])
      setActivities(a.status === 'fulfilled' ? a.value : [])
      setDeals(d.status === 'fulfilled' ? d.value : [])
      setProjetos(p.status === 'fulfilled' ? (p.value || []) : [])
      setTabsLoading(false)
    })
  }

  useEffect(() => { load() }, [contactId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getCrmMembers().then(setMembers).catch(() => {}) }, [])

  const reloadTabs = async () => {
    const [h, a, d, p] = await Promise.allSettled([
      getCrmContactHistory(contactId),
      getCrmContactActivities(contactId),
      getCrmContactDeals(contactId),
      pjListProjects().catch(() => []),
    ])
    setHistory(h.status === 'fulfilled' ? h.value : [])
    setActivities(a.status === 'fulfilled' ? a.value : [])
    setDeals(d.status === 'fulfilled' ? d.value : [])
    setProjetos(p.status === 'fulfilled' ? (p.value || []) : [])
  }

  const ownerName = useMemo(() => {
    if (!contact?.owner_id) return '—'
    return members.find((m) => m.id === contact.owner_id)?.nome || '—'
  }, [contact, members])

  const metrics = useMemo(() => {
    const won = deals
      .filter((d) => d.status === 'ganho')
      .map((d) => ({ valor: Number(d.valor || 0), when: d.data_fechamento || d.updated_at }))
      .filter((d) => d.when)
      .sort((a, b) => new Date(a.when) - new Date(b.when))
    const qtd = won.length
    const total = won.reduce((s, d) => s + d.valor, 0)
    const ticket = qtd > 0 ? total / qtd : 0
    const ultima = qtd > 0 ? won[qtd - 1].when : null
    return { qtd, total, ticket, ultima, ciclo: cicloDias(deals) }
  }, [deals])

  const clienteProjetos = useMemo(() => {
    const companyId = contact?.company_id
    return projetos.filter((p) => p.contact_id === contactId || (companyId && p.company_id === companyId))
  }, [projetos, contactId, contact])

  // casa evento do histórico (sem id) com a atividade real (com id), por
  // título+data — só usado pra habilitar "editar" nas notas da timeline.
  const activityIdByKey = useMemo(() => {
    const map = new Map()
    activities.forEach((a) => map.set(`${a.titulo}__${new Date(a.created_at).getTime()}`, a.id))
    return map
  }, [activities])

  const [dealToRemove, setDealToRemove] = useState(null)
  const [removing, setRemoving] = useState(false)
  const confirmRemoveDeal = async () => {
    if (!dealToRemove) return
    setRemoving(true); setErr(null)
    try { await deleteDeal(dealToRemove.id); await reloadTabs(); setDealToRemove(null) }
    catch (e) { setErr(e.message) } finally { setRemoving(false) }
  }

  const handleNewDeal = async () => {
    if (!contact) return
    let pipelineId = null, stageId = null
    try {
      const pls = await getPipelines()
      const def = pls.find((p) => p.is_default) || pls[0]
      if (def) {
        pipelineId = def.id
        const stages = await getStages(def.id)
        stageId = stages[0]?.id || null
      }
    } catch { /* segue sem funil */ }
    setDealModal({ pipelineId, stageId, initialContact: { id: contact.id, nome: contact.nome } })
  }

  const setOrigem = async (v) => {
    setContact((c) => ({ ...c, origem: v }))
    try { await updateCrmContact(contactId, { origem: v }) } catch (e) { setErr(e.message) }
  }

  const handleDelete = async () => {
    setDeleting(true); setErr(null)
    try { await deleteCrmContact(contactId); navigate('/financeiro/clientes') }
    catch (e) { setErr(e.message); setDeleting(false) }
  }

  if (!selectedClient) {
    return (
      <Layout title="Clientes" breadcrumb={[{ label: 'Financeiro' }, { label: 'Clientes', to: '/financeiro/clientes' }]}>
        <div className="cv-muted">Selecione um cliente para ver o perfil.</div>
      </Layout>
    )
  }

  const nome = contact?.nome || 'Cliente'
  const empresa = contact?.empresa || contact?.company_nome
  const temWhats = !!(contact?.telefone || contact?.whatsapp_id)

  const topbarRight = (
    <>
      <button type="button" className="cv-topbar-btn" onClick={handleNewDeal} disabled={!contact}>
        <Icon n="plus" s={15} sw={2.2} /> Novo negócio
      </button>
      <div className="cv-topbar-more-wrap">
        <button type="button" className="cv-topbar-more" onClick={() => setMoreOpen((v) => !v)} title="Mais ações">
          <Icon n="more" s={17} />
        </button>
        {moreOpen && (
          <div className="cv-topbar-menu" onMouseLeave={() => setMoreOpen(false)}>
            <button type="button" onClick={() => { setMoreOpen(false); setEditOpen(true) }}>
              <Icon n="edit" s={14} /> Editar cliente
            </button>
            <button type="button" className="danger" onClick={() => { setMoreOpen(false); setConfirmDelete(true) }}>
              <Icon n="trash" s={14} /> Excluir cliente
            </button>
          </div>
        )}
      </div>
    </>
  )

  return (
    <Layout
      title="Clientes"
      breadcrumb={[{ label: 'Clientes', to: '/financeiro/clientes' }, { label: nome }]}
      topbarRight={topbarRight}
    >
      {err && <div className="cv-error">{err}</div>}

      {loading ? (
        <div className="cv-muted">Carregando…</div>
      ) : !contact ? (
        <div className="cv-muted">Cliente não encontrado.</div>
      ) : (
        <div className="cv-wrap">
          {/* ── Coluna esquerda ─────────────────────────────────────────── */}
          <div className="cv-card cv-left">
            <div className="cv-identity">
              <div className="cv-avatar-wrap">
                <div className="cv-avatar" style={{ background: corAvatar(contact.id) }}>{iniciais(nome)}</div>
                {temWhats && <span className="cv-avatar-dot"><Icon n="whats" s={11} /></span>}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="cv-name">{nome}</div>
                <span className="cv-pill">Cliente</span>
              </div>
            </div>

            <div className="cv-rows">
              <div className="cv-row">
                <span className="cv-row-icon"><Icon n="user" s={14} /></span>
                <div><div className="cv-row-label">Proprietário</div><div className="cv-row-value">{ownerName}</div></div>
              </div>
              {contact.telefone && (
                <div className="cv-row">
                  <span className="cv-row-icon"><Icon n="phone" s={14} /></span>
                  <div><div className="cv-row-label">Telefone</div><div className="cv-row-value">{contact.telefone}</div></div>
                </div>
              )}
              {contact.telefone && (
                <div className="cv-row">
                  <span className="cv-row-icon whats"><Icon n="whats" s={14} /></span>
                  <div><div className="cv-row-label">WhatsApp</div><div className="cv-row-value whats">{contact.telefone}</div></div>
                </div>
              )}
            </div>

            <div className="cv-divider" />

            <div className="cv-label">Origem</div>
            <div className="cv-origem-wrap">
              <span className="cv-origem-icon"><Icon n={origemIcon(contact.origem)} s={15} /></span>
              <select className="cv-origem-select" value={contact.origem || ''} onChange={(e) => setOrigem(e.target.value)}>
                <option value="">— sem origem —</option>
                {[...new Set(['Indicação', 'Site', 'WhatsApp', 'Redes sociais', 'Evento', 'Cold outreach', 'Anúncio', 'Parceria', 'Outro', contact.origem].filter(Boolean))].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <button type="button" className="cv-editfields" onClick={() => setEditOpen(true)}>
              <Icon n="edit" s={13} /> Editar campos
            </button>

            <div className="cv-divider" />

            <div className="cv-label">Métricas</div>
            <div className="cv-metrics">
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="currency" s={13} /></span><span className="cv-metric-label">Ticket médio</span></div>
                <div className="cv-metric-value">{fmtBRL(metrics.ticket)}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="currency" s={13} /></span><span className="cv-metric-label">Total</span></div>
                <div className="cv-metric-value">{fmtBRL(metrics.total)}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="refresh" s={13} /></span><span className="cv-metric-label">Ciclo de compra</span></div>
                <div className="cv-metric-value">{metrics.ciclo != null ? `${metrics.ciclo} dias` : '—'}</div>
              </div>
              <div className="cv-metric">
                <div className="cv-metric-head"><span className="cv-metric-icon"><Icon n="calendar" s={13} /></span><span className="cv-metric-label">Última compra</span></div>
                <div className="cv-metric-value">{metrics.ultima ? fmtDate(metrics.ultima) : '—'}</div>
              </div>
            </div>

            <div className="cv-divider" />

            <div className="cv-label">Dados do cliente</div>
            <div className="cv-data-row">
              <span className="cv-data-label">Empresa</span>
              <span className="cv-data-value">{empresa || 'Não informada'}</span>
            </div>
            <div className="cv-data-row">
              <span className="cv-data-label">Tags</span>
              <span className="cv-data-value">
                {Array.isArray(contact.tags) && contact.tags.length ? (
                  <span className="cv-tags">{contact.tags.map((t, i) => <span key={i} className="cv-tag">{t}</span>)}</span>
                ) : 'Sem tags'}
              </span>
            </div>
          </div>

          {/* ── Coluna direita ──────────────────────────────────────────── */}
          <div className="cv-card cv-right">
            <div className="cv-tabs">
              <div className="cv-tabs-list">
                {TABS.map((t) => (
                  <button key={t.id} type="button" className={`cv-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                    {t.label}
                    {t.id === 'negocios' && deals.length > 0 && <span className="cv-tab-count">{deals.length}</span>}
                    {t.id === 'projetos' && clienteProjetos.length > 0 && <span className="cv-tab-count">{clienteProjetos.length}</span>}
                    {t.id === 'atividades' && activities.length > 0 && <span className="cv-tab-count">{activities.length}</span>}
                  </button>
                ))}
              </div>
              <button type="button" className="cv-tabs-close" onClick={() => navigate('/financeiro/clientes')} title="Voltar para Clientes">
                <Icon n="x" s={16} sw={2} />
              </button>
            </div>

            <div className="cv-tabbody">
              {tab === 'historico' && (
                <HistoricoTab
                  contactId={contactId} user={user} history={history} tabsLoading={tabsLoading}
                  activityIdByKey={activityIdByKey} onReload={reloadTabs} onError={setErr}
                />
              )}
              {tab === 'atividades' && (
                <ActivitiesTab
                  items={activities} loading={tabsLoading} members={members}
                  createActivity={(payload) => createCrmContactActivity(contactId, payload)}
                  onCreated={reloadTabs}
                />
              )}
              {tab === 'negocios' && (
                <DealsTab
                  items={deals} loading={tabsLoading}
                  onCreate={handleNewDeal}
                  onOpen={(id) => setInfoDealId(id)}
                  onRemove={setDealToRemove}
                  createLabel="Novo negócio"
                  emptyMsg="Este cliente ainda não possui negócios."
                />
              )}
              {tab === 'projetos' && (
                <ProjetosTab
                  items={clienteProjetos} loading={tabsLoading}
                  onOpen={(id) => navigate(`/projetos/${id}`)}
                />
              )}
              {tab === 'arquivos' && <FilesTab />}
              {tab === 'atendimentos' && <ServiceTab />}
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <ClienteFormPanel
          contactId={contactId}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load() }}
        />
      )}

      {infoDealId && (
        <DealPanel
          dealId={infoDealId}
          onClose={() => setInfoDealId(null)}
          onEdit={() => {}}
          onDeleted={async () => { setInfoDealId(null); await reloadTabs() }}
        />
      )}

      {dealModal && (
        <DealModal
          dealId={null}
          pipelineId={dealModal.pipelineId}
          stageId={dealModal.stageId}
          initialContact={dealModal.initialContact}
          onClose={() => setDealModal(null)}
          onSaved={() => { setDealModal(null); reloadTabs() }}
        />
      )}

      <ConfirmDialog
        open={!!dealToRemove}
        title="Remover negócio"
        message={dealToRemove ? `Remover o negócio "${dealToRemove.titulo || 'Negócio'}"? Esta ação não pode ser desfeita.` : ''}
        confirmLabel="Remover"
        danger
        busy={removing}
        onConfirm={confirmRemoveDeal}
        onCancel={() => { if (!removing) setDealToRemove(null) }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir cliente"
        message="Excluir este cliente? Os vínculos com negócios serão removidos. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setConfirmDelete(false) }}
      />
    </Layout>
  )
}
